import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Eye, Building2, Mail, Phone, MapPin, Upload, FileText, Check, Activity, BookOpen, AlertTriangle, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { entityClient } from '../services/entityClient';
import { useFocusEntity } from '../hooks/useFocusEntity';

const Customers = ({ user }) => {
  const { focusedId, registerRow } = useFocusEntity();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({
    national_address: null,
    cr: null,
    vat: null
  });
  // Phase 7.x · Fix #6 · Revenue Ledger Core — AR exposure + balances.
  const [exposure, setExposure] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // Map of customer_id → {total_invoiced, total_paid, outstanding_balance}
  // populated from /api/customers/_exposure so cards render balance chips.
  const balancesById = exposure.reduce((acc, row) => {
    acc[row.customer_id] = row;
    return acc;
  }, {});
  
  // Only Admin can delete customers
  const canDelete = user?.role === 'admin';
  
  const [formData, setFormData] = useState({
    name_arabic: '',
    name_english: '',
    address: '',
    national_address_file: '',
    cr_number: '',
    cr_file: '',
    vat_number: '',
    vat_file: '',
    contact_person: '',
    email: '',
    mobile: '',
    notes: '',
    linked_ar_account_id: ''
  });
  const navigate = useNavigate();
  const nationalAddressRef = useRef(null);
  const crFileRef = useRef(null);
  const vatFileRef = useRef(null);

  useEffect(() => {
    fetchCustomers();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchCustomers();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchCustomers = async () => {
    try {
      const [custRes, expRes, accRes] = await Promise.all([
        axiosInstance.get('/customers'),
        axiosInstance.get('/customers/_exposure').catch(() => ({ data: [] })),
        axiosInstance.get('/accounts').catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data);
      setExposure(expRes.data || []);
      setAccounts(accRes.data || []);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch customers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let customerId;
      if (editingCustomer) {
        await axiosInstance.put(`/customers/${editingCustomer.id}`, formData);
        customerId = editingCustomer.id;
        toast.success('Customer updated successfully');
      } else {
        const response = await axiosInstance.post('/customers', formData);
        customerId = response.data.id;
        // Check if needs approval
        if (response.data.approval_status === 'pending') {
          toast.success('Customer submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          toast.success('Customer added successfully');
        }
      }
      
      // Upload pending files after customer is created
      if (customerId) {
        for (const [fileType, file] of Object.entries(pendingFiles)) {
          if (file) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            try {
              await axiosInstance.post(
                `/customers/${customerId}/upload-file?file_type=${fileType}`,
                uploadFormData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
            } catch (uploadError) {
              console.error(`Failed to upload ${fileType} file:`, uploadError);
            }
          }
        }
      }
      
      setShowModal(false);
      resetForm();
      setPendingFiles({ national_address: null, cr: null, vat: null });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (customer) => {
    const id = typeof customer === 'string' ? customer : customer.id;
    const displayName = typeof customer === 'object'
      ? (customer.name_english || customer.name_arabic || customer.id)
      : undefined;
    const res = await entityClient.delete('customer', id, { displayName });
    if (res.mode === 'deleted') {
      toast.success('تم حذف العميل');
      fetchCustomers();
    } else if (res.mode === 'archived') {
      // Toast already shown by DeleteGuardProvider on archive success
      fetchCustomers();
    } else if (res.mode === 'error') {
      toast.error('فشل الحذف: ' + (res.error?.response?.data?.detail || res.error?.message || ''));
    }
    // 'cancelled' / 'blocked' → silent (user chose)
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name_arabic: customer.name_arabic || '',
      name_english: customer.name_english || '',
      address: customer.address || '',
      national_address_file: customer.national_address_file || '',
      cr_number: customer.cr_number || '',
      cr_file: customer.cr_file || '',
      vat_number: customer.vat_number || '',
      vat_file: customer.vat_file || '',
      contact_person: customer.contact_person || '',
      email: customer.email,
      mobile: customer.mobile || '',
      notes: customer.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name_arabic: '',
      name_english: '',
      address: '',
      national_address_file: '',
      cr_number: '',
      cr_file: '',
      vat_number: '',
      vat_file: '',
      contact_person: '',
      email: '',
      mobile: '',
      notes: ''
    });
    setEditingCustomer(null);
    setPendingFiles({ national_address: null, cr: null, vat: null });
  };

  const handleFileUpload = async (file, fileType) => {
    // If creating new customer, store file for later upload
    if (!editingCustomer) {
      setPendingFiles(prev => ({ ...prev, [fileType]: file }));
      toast.success(`${fileType.replace('_', ' ')} file selected - will be uploaded when you save`);
      return;
    }
    
    // If editing existing customer, upload immediately
    setUploadingFile(fileType);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axiosInstance.post(
        `/customers/${editingCustomer.id}/upload-file?file_type=${fileType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const fieldName = fileType === 'national_address' ? 'national_address_file' : `${fileType}_file`;
      setFormData(prev => ({ ...prev, [fieldName]: response.data.file_url }));
      setEditingCustomer(prev => ({ ...prev, [fieldName]: response.data.file_url }));
      toast.success('File uploaded successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDelete = async (fileType) => {
    if (!editingCustomer) return;
    
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const fieldName = fileType === 'national_address' ? 'national_address_file' : `${fileType}_file`;
      await axiosInstance.delete(`/customers/${editingCustomer.id}/delete-file?file_type=${fileType}`);
      
      setFormData(prev => ({ ...prev, [fieldName]: '' }));
      setEditingCustomer(prev => ({ ...prev, [fieldName]: '' }));
      toast.success('File deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    // Handle both old format (/uploads/...) and new format (/api/uploads/...)
    if (filePath.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
    }
    // Convert /uploads/customers/filename to /api/uploads/customers/filename
    return `${process.env.REACT_APP_BACKEND_URL}/api${filePath}`;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-12">Loading customers...</div>;
  }

  return (
    <div data-testid="customers-page">
      <div className="page-header">
        <h2 className="page-title">Customers</h2>
        <button
          data-testid="add-customer-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Customer
        </button>
      </div>

      {/* Phase 7.x · Fix #6 — AR Exposure Banner.
          Finance worklist of customers with non-zero outstanding
          balance. Click any row to jump to the customer's history
          (which already shows balance_due + invoices + payments). */}
      {exposure.length > 0 && (
        <div
          data-testid="ar-exposure-panel"
          className="mb-6 bg-rose-50/40 border border-rose-100 rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <h3 className="font-semibold text-rose-900">Outstanding AR · {exposure.length} customer(s)</h3>
            </div>
            <span className="text-xs text-rose-700">
              Total exposure: <b>{exposure.reduce((s, r) => s + (r.outstanding_balance || 0), 0).toLocaleString()} SAR</b>
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {exposure.slice(0, 12).map(row => (
              <button
                type="button"
                key={row.customer_id}
                data-testid={`ar-exposure-row-${row.customer_id}`}
                onClick={() => navigate(`/customers/${row.customer_id}/timeline`)}
                className="flex items-center justify-between gap-2 p-2 bg-white border border-rose-100 rounded-lg hover:border-rose-300 hover:shadow-sm transition text-right"
              >
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{row.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {row.customer_number}
                    {row.vat_number ? ` · ${row.vat_number}` : ''}
                  </p>
                </div>
                <span className="text-sm font-bold whitespace-nowrap text-rose-700">
                  {row.outstanding_balance.toLocaleString()} SAR
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {customers.length > 0 ? (
          customers.map((customer) => (
            <div
              key={customer.id}
              ref={registerRow(customer.id)}
              data-testid={`customer-card-${customer.id}`}
              className={`stat-card hover:shadow-xl transition-all cursor-pointer ${focusedId === customer.id ? 'focus-highlight' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="stat-icon blue">
                  <Building2 className="w-6 h-6" />
                </div>
                <div className="text-right">
                  <span className="status-badge active">{customer.status}</span>
                  <p className="text-xs text-gray-500 mt-1">{customer.customer_number}</p>
                </div>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-1">{customer.name_english}</h3>
              <p className="text-sm text-gray-600 mb-3" dir="rtl">{customer.name_arabic}</p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{customer.email}</span>
                </div>
                {customer.mobile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{customer.mobile}</span>
                  </div>
                )}
                {customer.contact_person && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building2 className="w-4 h-4" />
                    <span>{customer.contact_person}</span>
                  </div>
                )}
                {(customer.cr_number || customer.vat_number) && (
                  <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                    {customer.cr_number && (
                      <div><span className="font-semibold">CR:</span> {customer.cr_number}</div>
                    )}
                    {customer.vat_number && (
                      <div><span className="font-semibold">VAT:</span> {customer.vat_number}</div>
                    )}
                  </div>
                )}

                {/* Phase 7.x · Fix #6 — Outstanding AR chip (real-time
                    from /customers/_exposure). Only shows when the
                    customer has a non-zero balance — keeps clean cards
                    for fully-settled customers. */}
                {balancesById[customer.id] && (
                  <div
                    data-testid={`customer-balance-chip-${customer.id}`}
                    className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600 inline-flex items-center gap-1">
                      <Wallet className="w-3.5 h-3.5 text-rose-500" />
                      Outstanding
                    </span>
                    <span className="font-bold text-rose-700">
                      {balancesById[customer.id].outstanding_balance.toLocaleString()} SAR
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <button
                  data-testid={`view-customer-${customer.id}`}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  View
                </button>
                <button
                  data-testid={`timeline-customer-${customer.id}`}
                  onClick={() => navigate(`/customers/${customer.id}/timeline`)}
                  className="btn-icon"
                  title="Customer 360° Timeline"
                >
                  <Activity className="w-4 h-4 text-sky-600" />
                </button>
                {/* Phase 7.x · Fix #6 — Open AR Ledger (journals touching
                    this customer's receivable). Goes to the global Journal
                    page with a customer-scoped focus filter. */}
                <button
                  data-testid={`open-customer-ledger-${customer.id}`}
                  onClick={() => navigate(`/journal?focus=customer:${customer.id}`)}
                  className="btn-icon"
                  title="Open AR Ledger"
                >
                  <BookOpen className="w-4 h-4 text-indigo-600" />
                </button>
                <button
                  data-testid={`edit-customer-${customer.id}`}
                  onClick={() => handleEdit(customer)}
                  className="btn-icon edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {canDelete && (
                  <button
                    data-testid={`delete-customer-${customer.id}`}
                    onClick={() => handleDelete(customer)}
                    className="btn-icon delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500">
            <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">No customers found. Add your first customer!</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingCustomer ? 'Edit Customer' : 'New Customer'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={handleCloseModal}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* 1. Customer ID - Auto-generated, shown only when editing */}
              {editingCustomer && (
                <div className="form-group">
                  <label className="form-label">Customer ID</label>
                  <input
                    type="text"
                    value={editingCustomer.customer_number}
                    className="form-input bg-gray-100"
                    disabled
                    readOnly
                  />
                </div>
              )}

              {/* 2. Customer Name (Arabic) */}
              <div className="form-group">
                <label className="form-label">Customer Name (Arabic) *</label>
                <input
                  data-testid="customer-name-arabic-input"
                  type="text"
                  value={formData.name_arabic}
                  onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                  className="form-input"
                  placeholder="اسم العميل بالعربية"
                  dir="rtl"
                  required
                />
              </div>

              {/* 3. Customer Name (English) */}
              <div className="form-group">
                <label className="form-label">Customer Name (English) *</label>
                <input
                  data-testid="customer-name-english-input"
                  type="text"
                  value={formData.name_english}
                  onChange={(e) => setFormData({ ...formData, name_english: e.target.value })}
                  className="form-input"
                  placeholder="Customer Name in English"
                  required
                />
              </div>

              {/* 4. Address */}
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  data-testid="customer-address-input"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="form-input"
                  placeholder="Full address"
                />
              </div>

              {/* 4.1 National Address File */}
              <div className="form-group">
                <label className="form-label">Attached PDF - National Address</label>
                <div className="flex items-center gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    formData.national_address_file || pendingFiles.national_address ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'national_address')}
                      disabled={uploadingFile === 'national_address'}
                    />
                    {uploadingFile === 'national_address' ? (
                      <span className="text-purple-600">Uploading...</span>
                    ) : formData.national_address_file ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-600">File Uploaded</span>
                      </>
                    ) : pendingFiles.national_address ? (
                      <>
                        <Check className="w-5 h-5 text-orange-600" />
                        <span className="text-orange-600">{pendingFiles.national_address.name} (pending)</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-500">Click to upload PDF/Image</span>
                      </>
                    )}
                  </label>
                  {formData.national_address_file && (
                    <>
                      <a
                        href={getFileUrl(formData.national_address_file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                        title="View File"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleFileDelete('national_address')}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                        title="Delete File"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  {pendingFiles.national_address && !formData.national_address_file && (
                    <button
                      type="button"
                      onClick={() => setPendingFiles(prev => ({ ...prev, national_address: null }))}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      title="Remove File"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* 5. CR Number */}
              <div className="form-group">
                <label className="form-label">CR Number (Commercial Registration)</label>
                <input
                  data-testid="customer-cr-input"
                  type="text"
                  value={formData.cr_number}
                  onChange={(e) => setFormData({ ...formData, cr_number: e.target.value })}
                  className="form-input"
                  placeholder="Commercial Registration Number"
                />
              </div>

              {/* 5.1 CR File */}
              <div className="form-group">
                <label className="form-label">Attached PDF - CR (Commercial Registration)</label>
                <div className="flex items-center gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    formData.cr_file || pendingFiles.cr ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'cr')}
                      disabled={uploadingFile === 'cr'}
                    />
                    {uploadingFile === 'cr' ? (
                      <span className="text-purple-600">Uploading...</span>
                    ) : formData.cr_file ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-600">File Uploaded</span>
                      </>
                    ) : pendingFiles.cr ? (
                      <>
                        <Check className="w-5 h-5 text-orange-600" />
                        <span className="text-orange-600">{pendingFiles.cr.name} (pending)</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-500">Click to upload PDF/Image</span>
                      </>
                    )}
                  </label>
                  {formData.cr_file && (
                    <>
                      <a
                        href={getFileUrl(formData.cr_file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                        title="View File"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleFileDelete('cr')}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                        title="Delete File"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  {pendingFiles.cr && !formData.cr_file && (
                    <button
                      type="button"
                      onClick={() => setPendingFiles(prev => ({ ...prev, cr: null }))}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      title="Remove File"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* 6. VAT Number */}
              <div className="form-group">
                <label className="form-label">VAT Number</label>
                <input
                  data-testid="customer-vat-input"
                  type="text"
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  className="form-input"
                  placeholder="VAT Registration Number"
                />
              </div>

              {/* 6.1 VAT File */}
              <div className="form-group">
                <label className="form-label">Attached PDF - VAT Certificate</label>
                <div className="flex items-center gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    formData.vat_file || pendingFiles.vat ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'vat')}
                      disabled={uploadingFile === 'vat'}
                    />
                    {uploadingFile === 'vat' ? (
                      <span className="text-purple-600">Uploading...</span>
                    ) : formData.vat_file ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-600">File Uploaded</span>
                      </>
                    ) : pendingFiles.vat ? (
                      <>
                        <Check className="w-5 h-5 text-orange-600" />
                        <span className="text-orange-600">{pendingFiles.vat.name} (pending)</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-500">Click to upload PDF/Image</span>
                      </>
                    )}
                  </label>
                  {formData.vat_file && (
                    <>
                      <a
                        href={getFileUrl(formData.vat_file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                        title="View File"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleFileDelete('vat')}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                        title="Delete File"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  {pendingFiles.vat && !formData.vat_file && (
                    <button
                      type="button"
                      onClick={() => setPendingFiles(prev => ({ ...prev, vat: null }))}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      title="Remove File"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* 7. Attention To (Contact Person) */}
              <div className="form-group">
                <label className="form-label">Attention To (Contact Person)</label>
                <input
                  data-testid="customer-contact-person-input"
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="form-input"
                  placeholder="Contact person name"
                />
              </div>

              {/* 8. Email */}
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  data-testid="customer-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  placeholder="customer@example.com"
                  required
                />
              </div>

              {/* 9. Mobile */}
              <div className="form-group">
                <label className="form-label">Mobile</label>
                <input
                  data-testid="customer-mobile-input"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="form-input"
                  placeholder="Mobile number"
                />
              </div>

              {/* Notes - Optional field at the end */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="customer-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Optional notes about the customer"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-customer-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;